import apiClient from './apiClient';
import { Country } from './countryService';

export interface Currency {
  code: string;
  symbol: string;
}

export interface PackPrice {
  agencyAmount: number;
  id: number;
  amount: number;
  sabilyAmount: number;
  currency: Currency;
}

export interface Pack {
  id: number;
  productId: string;
  name: string;
  description: string;
  prices: PackPrice[];
  validityDuration: number;
  validityDurationUnit: 'days' | 'months' | 'years';
  dataValue: number;
  dataUnit: 'GB' | 'MB' |'KB';
  unlimited: boolean;
  status: 'available' | 'UNAVAILABLE';
  isActive: boolean;
  countries: Country[];
}
/**
 * Converts pack data value to a readable format (GB or MB) if the unit is KB
 * @param pack - The pack containing data value and unit
 * @returns Object with converted value and unit, or original if not KB
 */
export const convertDataValue = (pack: Pack): { value: number; unit: string } => {
  if (pack.dataUnit !== 'KB') {
    return { value: pack.dataValue, unit: pack.dataUnit };
  }

  const kb = pack.dataValue;
  
  // Convert to GB if >= 1GB (1,048,576 KB)
  if (kb >= 1048576) {
    return {
      value: Math.round((kb / 1048576) * 100) / 100,
      unit: 'GB'
    };
  }
  
  // Convert to MB if >= 1MB (1,024 KB)
  if (kb >= 1024) {
    return {
      value: Math.round((kb / 1024) * 100) / 100,
      unit: 'MB'
    };
  }
  
  // Keep as KB if less than 1MB
  return { value: kb, unit: 'KB' };
};

export interface PackFilters {
  name?: string;
  status?: string;
  priceMin?: number;
  priceMax?: number;
  dataValueMin?: number;
  dataValueMax?: number;
  validityDurationMin?: number;
  validityDurationMax?: number;
}

export interface PaginationParams {
  page?: number; 
  size?: number;
  filters?: PackFilters;
  sort?:string,
 
}

export const packService = {

  getAll: (params?: PaginationParams) => {
    const { page, size, filters } = params || {};

    return apiClient.get('/v1/packs/all', {
      params: {
        page,
        size,
        ...filters, 
      },
    });
  },

  getAllWithNameAndId:()=>{
    return apiClient.get('/v1/packs/all/id-name');
  },

  getStats: () =>
    apiClient.get('/v1/packs/stats'),

  getAllAdmin: (params?: PaginationParams) => {
    const { page, size,  sort,filters } = params || {};

    return apiClient.get('/v1/packs/all', {
      params: {
        page,
        size,
       sort,
        ...filters, 
      },
    });
  },

  getAllForAgency: (params?: PaginationParams) =>
    apiClient.get('/v1/packs/admin/all', { params }),

  create: (pack: Pack) =>
    apiClient.post('/v1/packs', pack),

  update: (id: number, pack: Pack) =>
    apiClient.put(`/v1/packs/${id}`, pack),

  updateStatus: (id: number, status: string) =>
    apiClient.put(`/v1/packs/updateStatus/${id}`, { status }),
    
  updatePrices: (id: number, prices: PackPrice[]) =>
    apiClient.put(`/prices/${id}`, { prices }),

  updatePrice: (packId: number, priceId: number, amount: number, isPublic: boolean) =>
    apiClient.put(
      `/v1/packs/update-price?packId=${packId}&priceId=${priceId}&amount=${amount}&public=${isPublic}`
    ),

  delete: (id: number) =>
    apiClient.delete(`/v1/packs/${id}`)
};

/**
 * Obtient le prix principal d'un pack (EUR par défaut)
 * @param pack - Le pack dont récupérer le prix
 * @param currencyCode - Le code de devise (par défaut 'EUR')
 * @returns Le prix du pack dans la devise spécifiée ou 0 si non trouvé
 */
export const getPackPrice = (pack: Pack, currencyCode: string = 'EUR'): number | null => {
  const price = pack.prices?.find(p => p.currency.code === currencyCode);
  return price?.sabilyAmount ?? null;
};
export const getPackAgencyPrice = (pack: Pack, currencyCode: string = 'EUR'): number | null => {
  const price = pack.prices?.find(p => p.currency.code === currencyCode);
 return price?.agencyAmount ?? null;
};
/**
 * Obtient le prix Sabily d'un pack (EUR par défaut)
 * @param pack - Le pack dont récupérer le prix Sabily
 * @param currencyCode - Le code de devise (par défaut 'EUR')
 * @returns Le prix Sabily du pack dans la devise spécifiée ou 0 si non trouvé
 */
export const getPackSabilyPrice = (pack: Pack, currencyCode: string = 'EUR'): number => {
  const price = pack.prices?.find(p => p.currency.code === currencyCode);
  return price ? price.sabilyAmount : 0;
};

/**
 * Obtient le symbole de devise d'un pack (EUR par défaut)
 * @param pack - Le pack dont récupérer le symbole de devise
 * @param currencyCode - Le code de devise (par défaut 'EUR')
 * @returns Le symbole de devise ou '€' si non trouvé
 */
export const getPackCurrencySymbol = (pack: Pack, currencyCode: string = 'EUR'): string => {
  const price = pack.prices?.find(p => p.currency.code === currencyCode);
  return price ? price.currency.symbol : '€';
};